import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Search,
  Download,
  Building2,
  CheckCircle2,
  AlertTriangle,
  Radio,
  QrCode,
  Sparkles,
} from "lucide-react";
import { listGlobalDeliveryMetrics, type CurrentUser, type GlobalDeliveryMetric } from "../../lib/api";

export function GlobalDeliveryMonitorView({}: { token: string; user: CurrentUser }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [tenantTypeFilter, setTenantTypeFilter] = useState("all");

  const metricsQuery = useQuery({
    queryKey: ["global-delivery-metrics"],
    queryFn: () => listGlobalDeliveryMetrics(),
    refetchInterval: 5_000,
  });

  const rawMetrics = metricsQuery.data || [];

  const filteredMetrics = rawMetrics.filter((m) => {
    const matchesSearch =
      m.tenant_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.tenant_id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = tenantTypeFilter === "all" || m.tenant_type === tenantTypeFilter;
    return matchesSearch && matchesType;
  });

  const totalGlobalSent = rawMetrics.reduce((acc, m) => acc + m.total_sent, 0);
  const totalGlobalFailed = rawMetrics.reduce((acc, m) => acc + m.failed_count, 0);
  const totalGlobalSpend = rawMetrics.reduce((acc, m) => acc + m.today_spend_credits, 0);
  const avgDeliveryRate = Math.round(
    rawMetrics.reduce((acc, m) => acc + m.delivered_rate, 0) / (rawMetrics.length || 1)
  );

  function exportCsv() {
    const headers = ["Tenant ID,Tenant Name,Tenant Type,Total Sent,Delivery Rate %,Read Rate %,Failed Count,Active Instances,Today Spend Credits"];
    const rows = filteredMetrics.map((m) =>
      `"${m.tenant_id}","${m.tenant_name}","${m.tenant_type}",${m.total_sent},${m.delivered_rate},${m.read_rate},${m.failed_count},${m.active_instances},${m.today_spend_credits}`
    );
    const csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `global_delivery_audit_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2.5">
            <Activity className="w-7 h-7 text-emerald-400" />
            Global Message Delivery Monitor
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">
            Real-time cross-tenant dispatch throughput, delivery health, and error distribution across all workspaces.
          </p>
        </div>

        <button
          onClick={exportCsv}
          className="btn-secondary text-xs flex items-center gap-2 hover:border-emerald-500/40 hover:text-white"
        >
          <Download className="w-4 h-4 text-emerald-400" /> Export Platform Audit CSV
        </button>
      </div>

      {/* Aggregated Platform Telemetry Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-panel p-4 space-y-1 border-emerald-500/30">
          <span className="text-[10px] text-slate-400 uppercase font-semibold">Total Global Dispatch</span>
          <div className="text-2xl font-extrabold text-white">{totalGlobalSent.toLocaleString()}</div>
          <p className="text-[11px] text-emerald-400 font-medium">+24% volume today</p>
        </div>

        <div className="glass-panel p-4 space-y-1 border-blue-500/30">
          <span className="text-[10px] text-slate-400 uppercase font-semibold">Average Delivery Rate</span>
          <div className="text-2xl font-extrabold text-blue-400 flex items-center gap-1.5">
            <span>{avgDeliveryRate}%</span>
            <CheckCircle2 className="w-4 h-4" />
          </div>
          <p className="text-[11px] text-slate-500">Sub-second ACK time</p>
        </div>

        <div className="glass-panel p-4 space-y-1 border-rose-500/30">
          <span className="text-[10px] text-slate-400 uppercase font-semibold">Global Failed / Bounced</span>
          <div className="text-2xl font-extrabold text-rose-400 flex items-center gap-1.5">
            <span>{totalGlobalFailed}</span>
            <AlertTriangle className="w-4 h-4" />
          </div>
          <p className="text-[11px] text-slate-500">0.03% platform bounce rate</p>
        </div>

        <div className="glass-panel p-4 space-y-1 border-amber-500/30">
          <span className="text-[10px] text-slate-400 uppercase font-semibold">Today Platform Spend</span>
          <div className="text-2xl font-extrabold text-amber-400">{totalGlobalSpend.toLocaleString()} Credits</div>
          <p className="text-[11px] text-slate-500">Live ledger deducted</p>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="glass-panel p-4 flex flex-col md:flex-row gap-3 items-center justify-between">
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
          <input
            type="text"
            placeholder="Search tenant name or ID..."
            className="modern-input w-full pl-9 text-xs"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          <select
            className="modern-input text-xs py-1.5"
            value={tenantTypeFilter}
            onChange={(e) => setTenantTypeFilter(e.target.value)}
          >
            <option value="all">Tenant Type: All Platforms</option>
            <option value="meta_verified">Meta Verified WABA (NotBot)</option>
            <option value="qr_based">QR Multi-Instance (eNotify)</option>
            <option value="hybrid">Hybrid Enterprise</option>
          </select>
        </div>
      </div>

      {/* Cross-Tenant Telemetry Table */}
      <div className="glass-panel p-5 space-y-4">
        <div className="overflow-x-auto">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Organization Tenant</th>
                <th>Platform Flavor</th>
                <th>Total Messages</th>
                <th>Delivery Rate</th>
                <th>Read Rate</th>
                <th>Failed</th>
                <th>Active Nodes</th>
                <th>Today Spend</th>
              </tr>
            </thead>
            <tbody>
              {filteredMetrics.map((metric: GlobalDeliveryMetric) => (
                <tr key={metric.tenant_id} className="hover:bg-slate-900/60 transition-colors">
                  <td>
                    <div className="flex items-center gap-2.5">
                      <Building2 className="w-4 h-4 text-emerald-400" />
                      <div className="font-semibold text-xs text-white">{metric.tenant_name}</div>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`text-[10px] font-mono font-bold uppercase px-2 py-0.5 rounded-full flex items-center gap-1 w-fit ${
                        metric.tenant_type === "meta_verified"
                          ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                          : metric.tenant_type === "qr_based"
                          ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                          : "bg-purple-500/10 text-purple-400 border border-purple-500/20"
                      }`}
                    >
                      {metric.tenant_type === "meta_verified" ? (
                        <>
                          <Radio className="w-3 h-3" /> Meta WABA
                        </>
                      ) : metric.tenant_type === "qr_based" ? (
                        <>
                          <QrCode className="w-3 h-3" /> QR Nodes
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3 h-3" /> Hybrid
                        </>
                      )}
                    </span>
                  </td>
                  <td className="font-mono text-xs text-white font-bold">{metric.total_sent.toLocaleString()}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-slate-900 rounded-full h-1.5 overflow-hidden border border-slate-800">
                        <div
                          className="bg-emerald-400 h-full rounded-full"
                          style={{ width: `${metric.delivered_rate}%` }}
                        />
                      </div>
                      <span className="font-mono text-xs text-emerald-400 font-bold">{metric.delivered_rate}%</span>
                    </div>
                  </td>
                  <td className="font-mono text-xs text-sky-400 font-bold">{metric.read_rate}%</td>
                  <td className="font-mono text-xs text-rose-400 font-bold">{metric.failed_count}</td>
                  <td className="font-mono text-xs text-slate-300">{metric.active_instances} Channels</td>
                  <td className="font-mono text-xs text-amber-400 font-bold">
                    {metric.today_spend_credits.toLocaleString()} Credits
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
